"use client";
// beui.dev/components/motion/input
// Restyled for Bavimail (see DESIGN.md §5 Inputs / Fields): inset well, hairline
// card border, 6px corners, Chivo Mono value, orange border + accent/20 ring on
// focus. No glow, no layout shift.

import { AnimatePresence, animate, motion, useReducedMotion } from "motion/react";
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";

export type InputClassNames = {
  root?: string;
  label?: string;
  field?: string;
  input?: string;
  leftIcon?: string;
  rightIcon?: string;
  successIcon?: string;
  errorMessage?: string;
};

/** `md` is the 46px form field, `sm` the 34px toolbar/search field. */
export type InputSize = "sm" | "md";
/**
 * `field` draws the inset well. `bare` draws nothing: for inputs embedded in a
 * composite control (chip editors, command bars) that owns the chrome itself.
 */
export type InputVariant = "field" | "bare";
/** Focus colour. `danger` is for type-to-confirm fields on destructive dialogs. */
export type InputTone = "accent" | "danger";

export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "defaultValue" | "onChange" | "size"
> {
  label?: ReactNode;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Truthy error triggers a shake, red border and (if a string) a message. */
  error?: string | boolean;
  /** Reserve one message line so validation does not shift nearby content. */
  reserveErrorLine?: boolean;
  success?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  size?: InputSize;
  variant?: InputVariant;
  tone?: InputTone;
  className?: string;
  classNames?: InputClassNames;
}

export type FieldState = "error" | "success" | "focused" | "idle";

/** Tracked mono label shared by `Input` and `Textarea`. */
export const FIELD_LABEL_CLASS =
  "font-mono-hb text-[10px] uppercase tracking-[0.15em] text-hb-muted";

/** Well chrome shared by `Input` and `Textarea`. */
export function fieldClassName({
  variant,
  tone,
  focused,
  hasError,
  disabled,
  className,
}: {
  variant: InputVariant;
  tone: InputTone;
  focused: boolean;
  hasError: boolean;
  disabled?: boolean;
  className?: string;
}) {
  // `flex` makes the control a flex item, so the parent's line box cannot pad the row.
  if (variant === "bare") return cn("relative flex", disabled && "opacity-50", className);
  return cn(
    "relative overflow-hidden rounded-sm border border-hb-card bg-hb-inset",
    "transition-colors duration-[var(--motion-duration-fast)]",
    focused && !hasError && tone === "accent" && "border-hb-accent ring-1 ring-hb-accent/20",
    (hasError || (focused && tone === "danger")) && "border-hb-danger ring-1 ring-hb-danger/20",
    disabled && "opacity-50",
    className,
  );
}

/** Shake a field once when its error appears. */
export function useErrorShake(hasError: boolean) {
  const fieldRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!fieldRef.current || reduce || !hasError) return;
    animate(fieldRef.current, { x: [0, -6, 6, -4, 4, -2, 0] }, { duration: 0.45 });
  }, [hasError, reduce]);

  return fieldRef;
}

/** Animated error row shared by `Input` and `Textarea`. */
export function FieldError({
  id,
  message,
  reserveErrorLine,
  className,
}: {
  id: string;
  message: string | null;
  reserveErrorLine: boolean;
  className?: string;
}) {
  const reduce = useReducedMotion();

  return (
    <div className={reserveErrorLine ? "min-h-4" : "contents"}>
      <AnimatePresence initial={false}>
        {message ? (
          <motion.p
            id={id}
            role="alert"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -4, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4, filter: "blur(4px)" }}
            transition={{ duration: 0.2 }}
            className={cn("font-mono-hb text-[11px] text-hb-danger-text", className)}
          >
            {message}
          </motion.p>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

const FIELD_HEIGHT: Record<InputSize, string> = { sm: "h-[34px]", md: "h-[46px]" };
const INPUT_TEXT: Record<InputSize, string> = { sm: "text-xs", md: "text-sm" };
const PAD_LEFT: Record<InputSize, [plain: string, icon: string]> = {
  sm: ["pl-3", "pl-9"],
  md: ["pl-4", "pl-10"],
};
const PAD_RIGHT: Record<InputSize, [plain: string, icon: string]> = {
  sm: ["pr-3", "pr-9"],
  md: ["pr-4", "pr-10"],
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    value: valueProp,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    error,
    reserveErrorLine = false,
    success,
    leftIcon,
    rightIcon,
    size = "md",
    variant = "field",
    tone = "accent",
    className,
    classNames,
    disabled,
    id: idProp,
    type,
    ...rest
  },
  ref,
) {
  const reactId = useId();
  const id = idProp ?? reactId;
  const reduce = useReducedMotion();

  const controlled = valueProp !== undefined;
  const [internal, setInternal] = useState(defaultValue ?? "");
  const value = controlled ? (valueProp ?? "") : internal;

  const [focused, setFocused] = useState(false);

  const hasError = Boolean(error);
  const errorMessage = typeof error === "string" ? error : null;
  const fieldRef = useErrorShake(hasError);

  // Right edge shows the success check, otherwise the caller's right icon.
  const rightSlot = success ? null : rightIcon;
  const bare = variant === "bare";

  const handleChange = (next: string) => {
    if (!controlled) setInternal(next);
    onChange?.(next);
  };

  return (
    <div className={cn("flex flex-col gap-2", className, classNames?.root)}>
      {label ? (
        <label htmlFor={id} className={cn(FIELD_LABEL_CLASS, classNames?.label)}>
          {label}
        </label>
      ) : null}

      <div
        ref={fieldRef}
        data-state={
          (hasError ? "error" : success ? "success" : focused ? "focused" : "idle") as FieldState
        }
        className={fieldClassName({
          variant,
          tone,
          focused,
          hasError,
          disabled,
          className: cn(!bare && FIELD_HEIGHT[size], classNames?.field),
        })}
      >
        {leftIcon ? (
          <span
            className={cn(
              "pointer-events-none absolute top-1/2 flex -translate-y-1/2 items-center text-hb-faint [&_svg]:h-3.5 [&_svg]:w-3.5",
              bare ? "left-0" : "left-3",
              classNames?.leftIcon,
            )}
          >
            {leftIcon}
          </span>
        ) : null}

        <input
          ref={ref}
          id={id}
          type={type}
          value={value}
          disabled={disabled}
          aria-invalid={hasError || undefined}
          aria-describedby={errorMessage ? `${id}-error` : undefined}
          {...rest}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={(event) => {
            setFocused(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setFocused(false);
            onBlur?.(event);
          }}
          className={cn(
            "peer w-full bg-transparent text-hb-heading caret-hb-accent outline-none",
            // Bare inputs size themselves from their line-height, like a plain <input>.
            !bare && "h-full",
            "placeholder:text-hb-placeholder",
            INPUT_TEXT[size],
            !bare && "font-mono-hb",
            !bare && PAD_LEFT[size][leftIcon ? 1 : 0],
            !bare && PAD_RIGHT[size][rightSlot || success ? 1 : 0],
            bare && leftIcon && "pl-6",
            bare && (rightSlot || success) && "pr-6",
            disabled && "cursor-not-allowed",
            classNames?.input,
          )}
        />

        {success ? (
          <motion.svg
            viewBox="0 0 24 24"
            fill="none"
            className={cn(
              "absolute top-1/2 h-4 w-4 -translate-y-1/2 text-hb-success",
              bare ? "right-0" : "right-3.5",
              classNames?.successIcon,
            )}
          >
            <motion.path
              d="M5 12.5l4.5 4.5L19 7.5"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={reduce ? { pathLength: 1 } : { pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            />
          </motion.svg>
        ) : rightSlot ? (
          <span
            className={cn(
              "absolute right-0 top-0 flex h-full items-center text-hb-faint [&_button]:grid [&_button]:h-full [&_button]:w-10 [&_button]:place-items-center [&_svg]:h-3.5 [&_svg]:w-3.5",
              classNames?.rightIcon,
            )}
          >
            {rightSlot}
          </span>
        ) : null}
      </div>

      <FieldError
        id={`${id}-error`}
        message={errorMessage}
        reserveErrorLine={reserveErrorLine}
        className={classNames?.errorMessage}
      />
    </div>
  );
});
